import { PDF_MAX_PAGES, PDF_ORIENTATIONS, printableAreaMm } from './images-to-pdf';

/**
 * 子が作るページ画像の最大幅（px）。縦向きでも高さが親の上限（`PDF_PAGE_IMAGE_MAX_SIDE_PX`）に
 * 収まるよう、幅は上限の半分にする。
 */
const PAGE_IMAGE_MAX_WIDTH_PX = 4096;
const PIXEL_RATIO = 2;

/**
 * wrapper が注入する `sazaeDashboard.exportPdf` の実装（関数を返す JS の式）を返す。
 *
 * sandbox（`allow-same-origin` なし）では html2canvas のように入れ子の iframe へ DOM を複製する
 * ライブラリが動かないため、iframe を使わない html-to-image で要素を SVG 画像にし、A4 の印刷範囲の
 * 縦横比でページごとの PNG に切り出して親へ transfer する。PDF は親が組み立てる（`images-to-pdf.ts`）。
 * html-to-image は vendor（CSP の `script-src` に含まれる）から初回の呼び出し時に読み込む。
 */
export const buildPdfExportSource = (nonce: string, htmlToImageUrl: string): string => {
  const pageAspects = Object.fromEntries(
    PDF_ORIENTATIONS.map((orientation) => {
      const area = printableAreaMm(orientation);
      return [orientation, area.height / area.width];
    }),
  );
  return `(() => {
    const nonce = ${JSON.stringify(nonce)};
    const htmlToImageUrl = ${JSON.stringify(htmlToImageUrl)};
    const pageAspects = ${JSON.stringify(pageAspects)};
    const maxPages = ${PDF_MAX_PAGES};
    let htmlToImageLoading = null;
    const loadHtmlToImage = () => {
      htmlToImageLoading ??= new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = htmlToImageUrl;
        script.onload = () => resolve(window.htmlToImage);
        script.onerror = () => {
          htmlToImageLoading = null;
          reject(new Error('PDF の作成に使うライブラリを読み込めませんでした'));
        };
        document.head.append(script);
      });
      return htmlToImageLoading;
    };
    // ページの下端にかかる表の行・グラフ（canvas / svg）・画像があれば、その上端で改ページする。
    // それがページより高いとき（上端がページの上端にある）だけ途中で切る。
    const splitPages = (element, totalHeight, pageHeight) => {
      const origin = element.getBoundingClientRect().top;
      const blocks = Array.from(element.querySelectorAll('tr, canvas, svg, img'), (block) => {
        const rect = block.getBoundingClientRect();
        return [rect.top - origin, rect.bottom - origin];
      });
      const ranges = [];
      let start = 0;
      while (totalHeight - start > pageHeight && ranges.length <= maxPages) {
        let end = start + pageHeight;
        let moved = true;
        // 入れ子の表や表の中のグラフでは外側の要素も境目にかかり得るため、かかる要素が無くなるまで繰り返す。
        while (moved) {
          moved = false;
          for (const [blockTop, blockBottom] of blocks) {
            if (blockTop > start + 1 && blockTop < end - 0.5 && blockBottom > end + 0.5) {
              end = blockTop;
              moved = true;
            }
          }
        }
        ranges.push([start, end]);
        start = end;
      }
      ranges.push([start, totalHeight]);
      return ranges;
    };
    const toPng = (canvas) =>
      new Promise((resolve, reject) =>
        canvas.toBlob(
          (blob) => (blob ? resolve(blob.arrayBuffer()) : reject(new Error('ページ画像を作れませんでした'))),
          'image/png',
        ),
      );
    return async ({ filename, element = document.body, orientation = 'landscape' } = {}) => {
      if (typeof filename !== 'string') throw new TypeError('filename must be a string');
      if (!(element instanceof Element)) throw new TypeError('element must be an Element');
      if (!Object.hasOwn(pageAspects, orientation)) {
        throw new TypeError("orientation must be 'landscape' or 'portrait'");
      }
      const htmlToImage = await loadHtmlToImage();
      // Web フォントの埋め込みは fetch が要り connect-src 'none' で止まるため行わない。
      const svg = await htmlToImage.toSvg(element, { skipFonts: true, backgroundColor: '#fff' });
      const image = new Image();
      image.src = svg;
      await image.decode();
      const width = image.naturalWidth;
      const height = image.naturalHeight;
      if (width === 0 || height === 0) throw new RangeError('element has no size');
      const ranges = splitPages(element, height, width * pageAspects[orientation]);
      if (ranges.length > maxPages) throw new RangeError('PDF は ' + maxPages + ' ページまでです');
      // 全体を 1 枚の canvas にすると長いダッシュボードで canvas の上限に当たるため、ページごとに描く。
      const ratio = Math.min(${PIXEL_RATIO}, ${PAGE_IMAGE_MAX_WIDTH_PX} / width);
      const pages = [];
      for (const [start, end] of ranges) {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * ratio));
        canvas.height = Math.max(1, Math.round((end - start) * ratio));
        canvas
          .getContext('2d')
          .drawImage(image, 0, start, width, end - start, 0, 0, canvas.width, canvas.height);
        pages.push(await toPng(canvas));
      }
      window.parent.postMessage(
        { kind: 'dashboard-download', nonce, filename, pdf: { orientation, pages } },
        '*',
        pages,
      );
    };
  })()`;
};
